import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Tabs,
  Tab,
  TextField,
  Switch,
  IconButton,
  Chip,
  Alert,
  RadioGroup,
  Radio,
  FormControlLabel,
  MenuItem,
  Tooltip,
  alpha,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { toast } from 'react-toastify';
import proxy from '../../api/axios';
import { formatScientificText } from '../../common/formatScientificText';
import {
  ACCENT,
  AdminFormSection,
  AdminLoading,
  AdminPage,
  adminFieldSx,
  useAdminPageTheme,
} from './adminUi';

const SAMPLE_JSON = `{
  "title": "NEET Full Mock Test 1",
  "description": "Full syllabus mock, 180 minutes",
  "durationMinutes": 180,
  "startTime": "2026-09-01T09:00:00.000Z",
  "endTime": "2026-09-01T18:00:00.000Z",
  "marksCorrect": 4,
  "marksIncorrect": 1,
  "allowNegativeMarking": true,
  "questions": [
    {
      "type": "mcq",
      "subject": "Physics",
      "questionText": "What is the SI unit of force?",
      "options": ["Joule", "Newton", "Watt", "Pascal"],
      "correctOptionIndex": 1,
      "explanation": "Force = mass \\u00d7 acceleration, unit N."
    },
    {
      "type": "fill-in-blank",
      "subject": "Chemistry",
      "questionText": "The chemical formula of water is ___.",
      "correctAnswers": ["H2O"],
      "caseSensitive": false,
      "negativeMarking": false,
      "marksCorrect": 2
    }
  ]
}`;

const NEET_QUESTION_TYPES = ['mcq', 'fill-in-blank'];
const isFiniteNumber = (value) => typeof value === 'number' && Number.isFinite(value);
const makeKey = () => `q_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

/**
 * Mirrors backend/src/services/neetTestService.js's validateTestPayload so
 * admins see the real, specific validation errors immediately — before
 * hitting the server. The backend re-validates authoritatively regardless;
 * this is purely for a faster feedback loop, so keep the two in sync by hand
 * if the schema ever changes. Operates on the wire schema shape (correctAnswers
 * as an array etc), not the form-editor's editing shape.
 */
const validateNeetTestPayload = (payload) => {
  const errors = [];
  if (!payload || typeof payload !== 'object') {
    return ['Payload must be a JSON object.'];
  }
  if (!String(payload.title || '').trim()) {
    errors.push('title is required.');
  }
  const durationMinutes = Number(payload.durationMinutes);
  if (!isFiniteNumber(durationMinutes) || durationMinutes <= 0) {
    errors.push('durationMinutes must be a positive number.');
  }
  const startTime = payload.startTime ? new Date(payload.startTime) : null;
  const endTime = payload.endTime ? new Date(payload.endTime) : null;
  if (!startTime || Number.isNaN(startTime.getTime())) {
    errors.push('startTime is required and must be a valid date.');
  }
  if (!endTime || Number.isNaN(endTime.getTime())) {
    errors.push('endTime is required and must be a valid date.');
  }
  if (startTime && endTime && !Number.isNaN(startTime.getTime()) && !Number.isNaN(endTime.getTime())) {
    if (endTime <= startTime) {
      errors.push('endTime must be after startTime.');
    } else if (isFiniteNumber(durationMinutes) && endTime.getTime() - startTime.getTime() < durationMinutes * 60 * 1000) {
      errors.push('The window between startTime and endTime must be at least as long as durationMinutes.');
    }
  }
  if (payload.marksCorrect !== undefined && (!isFiniteNumber(Number(payload.marksCorrect)) || Number(payload.marksCorrect) < 0)) {
    errors.push('marksCorrect must be a non-negative number.');
  }
  if (payload.marksIncorrect !== undefined && (!isFiniteNumber(Number(payload.marksIncorrect)) || Number(payload.marksIncorrect) < 0)) {
    errors.push('marksIncorrect must be a non-negative number.');
  }

  const questions = Array.isArray(payload.questions) ? payload.questions : null;
  if (!questions || questions.length === 0) {
    errors.push('questions must be a non-empty array.');
  } else {
    questions.forEach((q, idx) => {
      const label = `Question ${idx + 1}`;
      if (!q || typeof q !== 'object') {
        errors.push(`${label}: must be an object.`);
        return;
      }
      if (!NEET_QUESTION_TYPES.includes(q.type)) {
        errors.push(`${label}: type must be one of ${NEET_QUESTION_TYPES.join(', ')}.`);
        return;
      }
      if (!String(q.questionText || '').trim()) {
        errors.push(`${label}: questionText is required.`);
      }
      if (q.marksCorrect !== undefined && (!isFiniteNumber(Number(q.marksCorrect)) || Number(q.marksCorrect) < 0)) {
        errors.push(`${label}: marksCorrect must be a non-negative number.`);
      }
      if (q.marksIncorrect !== undefined && (!isFiniteNumber(Number(q.marksIncorrect)) || Number(q.marksIncorrect) < 0)) {
        errors.push(`${label}: marksIncorrect must be a non-negative number.`);
      }
      if (q.type === 'mcq') {
        // An option counts as filled if it has text OR an image at that index
        // (graph/diagram-identification questions can be image-only options).
        const optionImages = Array.isArray(q.optionImages) ? q.optionImages : [];
        if (
          !Array.isArray(q.options) ||
          q.options.length !== 4 ||
          q.options.some((o, i) => !String(o || '').trim() && !String(optionImages[i] || '').trim())
        ) {
          errors.push(`${label}: mcq requires exactly 4 options, each with text and/or an image.`);
        }
        const correctOptionIndex = Number(q.correctOptionIndex);
        if (!Number.isInteger(correctOptionIndex) || correctOptionIndex < 0 || correctOptionIndex > 3) {
          errors.push(`${label}: correctOptionIndex must be an integer between 0 and 3.`);
        }
      } else if (q.type === 'fill-in-blank') {
        if (
          !Array.isArray(q.correctAnswers) ||
          q.correctAnswers.length === 0 ||
          q.correctAnswers.some((a) => !String(a || '').trim())
        ) {
          errors.push(`${label}: fill-in-blank requires a non-empty correctAnswers array of non-empty strings.`);
        }
      }
    });
  }
  return errors;
};

/** Live syntax+schema check for the raw "Paste JSON" textarea specifically. */
const parseForPreview = (raw) => {
  if (!raw || !raw.trim()) return null;
  try {
    const obj = JSON.parse(raw);
    const validationErrors = validateNeetTestPayload(obj);
    const questions = Array.isArray(obj.questions) ? obj.questions : [];
    const bySubject = questions.reduce((acc, q) => {
      const s = q.subject || 'Unspecified';
      acc[s] = (acc[s] || 0) + 1;
      return acc;
    }, {});
    const byType = questions.reduce((acc, q) => {
      acc[q.type] = (acc[q.type] || 0) + 1;
      return acc;
    }, {});
    return {
      ok: validationErrors.length === 0,
      obj,
      questionCount: questions.length,
      bySubject,
      byType,
      validationErrors,
    };
  } catch (error) {
    return { ok: false, error: error.message };
  }
};

/**
 * The Form Editor / Preview tabs work on a friendlier "editing shape" that's
 * always structurally complete (no undefined arrays) and keeps fill-in-blank
 * answers as one freely-typeable comma string instead of an array, so a
 * trailing comma while typing doesn't get silently eaten. normalize/denormalize
 * convert to and from the actual wire schema (used for JSON display + submit).
 */
const normalizeQuestion = (q) => {
  const src = q && typeof q === 'object' ? q : {};
  const type = src.type === 'fill-in-blank' ? 'fill-in-blank' : 'mcq';
  const base = {
    _key: makeKey(),
    type,
    subject: src.subject || '',
    questionText: src.questionText || '',
    marksCorrect: src.marksCorrect,
    marksIncorrect: src.marksIncorrect,
    negativeMarking: src.negativeMarking,
    explanation: src.explanation || '',
  };
  if (type === 'fill-in-blank') {
    return {
      ...base,
      correctAnswersText:
        src.correctAnswersText !== undefined
          ? src.correctAnswersText
          : Array.isArray(src.correctAnswers)
            ? src.correctAnswers.join(', ')
            : '',
      caseSensitive: !!src.caseSensitive,
    };
  }
  return {
    ...base,
    options: Array.isArray(src.options) && src.options.length === 4 ? [...src.options] : ['', '', '', ''],
    correctOptionIndex: Number.isInteger(src.correctOptionIndex) ? src.correctOptionIndex : 0,
  };
};

// Mirrors CreateContest.js's own toLocalInput — converts a server ISO date
// (or any parseable date string) into the `datetime-local` input format.
const toLocalInput = (dateTime) => {
  if (!dateTime) return '';
  const date = new Date(dateTime);
  if (Number.isNaN(date.getTime())) return '';
  return date
    .toLocaleString('sv-SE', { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone, hour12: false })
    .substring(0, 16);
};

const normalizePayload = (obj) => ({
  title: obj?.title || '',
  description: obj?.description || '',
  durationMinutes: obj?.durationMinutes ?? '',
  startTime: toLocalInput(obj?.startTime),
  endTime: toLocalInput(obj?.endTime),
  marksCorrect: obj?.marksCorrect ?? 4,
  marksIncorrect: obj?.marksIncorrect ?? 1,
  allowNegativeMarking: obj?.allowNegativeMarking !== undefined ? !!obj.allowNegativeMarking : true,
  questions: Array.isArray(obj?.questions) ? obj.questions.map(normalizeQuestion) : [],
});

const denormalizeQuestion = (q) => {
  const base = {
    type: q.type,
    questionText: q.questionText,
  };
  if (String(q.subject || '').trim()) base.subject = q.subject.trim();
  if (String(q.explanation || '').trim()) base.explanation = q.explanation.trim();
  if (q.marksCorrect !== undefined && q.marksCorrect !== '') base.marksCorrect = Number(q.marksCorrect);
  if (q.marksIncorrect !== undefined && q.marksIncorrect !== '') base.marksIncorrect = Number(q.marksIncorrect);
  if (q.negativeMarking !== undefined) base.negativeMarking = q.negativeMarking;

  if (q.type === 'fill-in-blank') {
    return {
      ...base,
      correctAnswers: (q.correctAnswersText || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
      caseSensitive: !!q.caseSensitive,
    };
  }
  return {
    ...base,
    options: q.options || [],
    correctOptionIndex: Number(q.correctOptionIndex),
  };
};

const denormalizePayload = (payload) => ({
  title: payload.title,
  description: payload.description,
  durationMinutes: payload.durationMinutes === '' ? undefined : Number(payload.durationMinutes),
  startTime: payload.startTime || undefined,
  endTime: payload.endTime || undefined,
  marksCorrect: payload.marksCorrect === '' ? undefined : Number(payload.marksCorrect),
  marksIncorrect: payload.marksIncorrect === '' ? undefined : Number(payload.marksIncorrect),
  allowNegativeMarking: payload.allowNegativeMarking,
  questions: (payload.questions || []).map(denormalizeQuestion),
});

const createBlankQuestion = (type) => normalizeQuestion({ type, options: ['', '', '', ''] });

const NEGATIVE_MARKING_OPTIONS = [
  { value: '', label: 'Inherit test default' },
  { value: 'true', label: 'On — subtract marks' },
  { value: 'false', label: 'Off — no penalty' },
];

const QuestionEditorCard = ({ question, index, total, onChange, onRemove, onMoveUp, onMoveDown }) => {
  const { border, muted, surface } = useAdminPageTheme();
  const isMcq = question.type === 'mcq';

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2, border: `1px solid ${border}`, bgcolor: surface, mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5, flexWrap: 'wrap' }}>
        <Chip size="small" label={`Q${index + 1}`} sx={{ fontWeight: 800 }} />
        <Chip size="small" variant="outlined" label={isMcq ? 'MCQ' : 'Fill in the blank'} sx={{ fontWeight: 700 }} />
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Move up">
          <span>
            <IconButton size="small" disabled={index === 0} onClick={onMoveUp}>
              <ArrowUpwardIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Move down">
          <span>
            <IconButton size="small" disabled={index === total - 1} onClick={onMoveDown}>
              <ArrowDownwardIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Delete question">
          <IconButton size="small" color="error" onClick={onRemove}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '160px 1fr' }, gap: 1.25, mb: 1.25 }}>
        <TextField
          size="small"
          label="Subject"
          value={question.subject}
          onChange={(e) => onChange({ subject: e.target.value })}
          sx={adminFieldSx}
        />
        <TextField
          size="small"
          label="Question text"
          value={question.questionText}
          onChange={(e) => onChange({ questionText: e.target.value })}
          multiline
          sx={adminFieldSx}
        />
      </Box>

      {isMcq ? (
        <Box sx={{ mb: 1.25 }}>
          <Typography variant="caption" sx={{ color: muted, fontWeight: 700, display: 'block', mb: 0.5 }}>
            Options — select the correct one
          </Typography>
          <RadioGroup
            value={String(question.correctOptionIndex)}
            onChange={(e) => onChange({ correctOptionIndex: Number(e.target.value) })}
          >
            {question.options.map((opt, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mb: 0.75 }}>
                <FormControlLabel
                  value={String(i)}
                  control={<Radio size="small" sx={{ color: ACCENT, '&.Mui-checked': { color: ACCENT } }} />}
                  label=""
                  sx={{ mr: 0 }}
                />
                <TextField
                  size="small"
                  fullWidth
                  placeholder={`Option ${i + 1}`}
                  value={opt}
                  onChange={(e) => {
                    const options = [...question.options];
                    options[i] = e.target.value;
                    onChange({ options });
                  }}
                  sx={adminFieldSx}
                />
              </Box>
            ))}
          </RadioGroup>
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr auto' }, gap: 1.25, alignItems: 'flex-start', mb: 1.25 }}>
          <TextField
            size="small"
            label="Accepted answers (comma-separated)"
            value={question.correctAnswersText}
            onChange={(e) => onChange({ correctAnswersText: e.target.value })}
            helperText="e.g. H2O, H₂O — any of these count as correct"
            sx={adminFieldSx}
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={question.caseSensitive}
                onChange={(e) => onChange({ caseSensitive: e.target.checked })}
              />
            }
            label="Case sensitive"
            sx={{ mt: { xs: 0, sm: 0.5 } }}
          />
        </Box>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr' }, gap: 1.25, mb: 1.25 }}>
        <TextField
          size="small"
          type="number"
          label="Marks (override)"
          value={question.marksCorrect ?? ''}
          onChange={(e) => onChange({ marksCorrect: e.target.value === '' ? undefined : e.target.value })}
          placeholder="Test default"
          sx={adminFieldSx}
        />
        <TextField
          size="small"
          type="number"
          label="Negative marks (override)"
          value={question.marksIncorrect ?? ''}
          onChange={(e) => onChange({ marksIncorrect: e.target.value === '' ? undefined : e.target.value })}
          placeholder="Test default"
          sx={adminFieldSx}
        />
        <TextField
          size="small"
          select
          label="Negative marking"
          value={question.negativeMarking === undefined ? '' : String(question.negativeMarking)}
          onChange={(e) => onChange({ negativeMarking: e.target.value === '' ? undefined : e.target.value === 'true' })}
          sx={adminFieldSx}
        >
          {NEGATIVE_MARKING_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <TextField
        size="small"
        fullWidth
        label="Explanation (optional, shown on result review)"
        value={question.explanation}
        onChange={(e) => onChange({ explanation: e.target.value })}
        sx={adminFieldSx}
      />
    </Box>
  );
};

const TestPreview = ({ payload }) => {
  const { border, muted, isDark, surface } = useAdminPageTheme();
  const questions = Array.isArray(payload.questions) ? payload.questions : [];
  const defaultMarksCorrect = payload.marksCorrect === '' ? 4 : Number(payload.marksCorrect) || 4;
  const defaultMarksIncorrect = payload.marksIncorrect === '' ? 1 : Number(payload.marksIncorrect) || 1;
  const totalMarks = questions.reduce(
    (sum, q) => sum + (q.marksCorrect !== undefined && q.marksCorrect !== '' ? Number(q.marksCorrect) : defaultMarksCorrect),
    0,
  );

  if (questions.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: muted }}>
        No questions yet — add some in the Form Editor, or paste/upload JSON, then come back here.
      </Typography>
    );
  }

  return (
    <Box>
      <Box sx={{ p: 2, borderRadius: 2, border: `1px solid ${border}`, bgcolor: surface, mb: 2 }}>
        <Typography sx={{ fontWeight: 900, fontSize: '1.1rem', mb: 0.5 }}>{payload.title || 'Untitled test'}</Typography>
        {payload.description && (
          <Typography variant="body2" sx={{ color: muted, mb: 1 }}>
            {payload.description}
          </Typography>
        )}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          <Chip size="small" label={`${payload.durationMinutes || '?'} minutes`} sx={{ fontWeight: 700 }} />
          <Chip size="small" label={`${questions.length} question${questions.length === 1 ? '' : 's'}`} sx={{ fontWeight: 700 }} />
          <Chip size="small" label={`${totalMarks} marks`} sx={{ fontWeight: 700 }} />
          <Chip
            size="small"
            label={`+${defaultMarksCorrect} / ${payload.allowNegativeMarking ? `-${defaultMarksIncorrect}` : 'no negative marking'}`}
            sx={{ fontWeight: 700, bgcolor: alpha(ACCENT, isDark ? 0.22 : 0.1), color: ACCENT }}
          />
          {payload.startTime && payload.endTime ? (
            <Chip
              size="small"
              label={`Open ${new Date(payload.startTime).toLocaleString()} → ${new Date(payload.endTime).toLocaleString()}`}
              sx={{ fontWeight: 700 }}
            />
          ) : (
            <Chip size="small" label="Schedule not set" color="warning" variant="outlined" sx={{ fontWeight: 700 }} />
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        {questions.map((q, index) => {
          const marksCorrect = q.marksCorrect !== undefined && q.marksCorrect !== '' ? Number(q.marksCorrect) : defaultMarksCorrect;
          const marksIncorrect = q.marksIncorrect !== undefined && q.marksIncorrect !== '' ? Number(q.marksIncorrect) : defaultMarksIncorrect;
          const negativeMarking = q.negativeMarking !== undefined ? q.negativeMarking : payload.allowNegativeMarking;
          return (
            <Box key={q._key || index} sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2, border: `1px solid ${border}`, bgcolor: surface }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1, flexWrap: 'wrap' }}>
                <Chip size="small" label={`Q${index + 1}`} sx={{ fontWeight: 800 }} />
                {q.subject && <Chip size="small" variant="outlined" label={q.subject} sx={{ fontWeight: 700 }} />}
                <Chip size="small" label={q.type === 'mcq' ? 'MCQ' : 'Fill in the blank'} sx={{ fontWeight: 700 }} />
                <Typography sx={{ ml: 'auto', fontSize: '0.78rem', fontWeight: 700, color: muted }}>
                  +{marksCorrect}
                  {negativeMarking ? ` / -${marksIncorrect}` : ' / no penalty'}
                </Typography>
              </Box>
              {q.questionText ? (
                <Typography
                  sx={{ fontWeight: 700, fontSize: { xs: '0.9rem', sm: '0.95rem' }, mb: 1.25 }}
                  dangerouslySetInnerHTML={{ __html: formatScientificText(q.questionText) }}
                />
              ) : (
                <Typography sx={{ fontWeight: 700, fontSize: { xs: '0.9rem', sm: '0.95rem' }, mb: 1.25 }}>
                  <em style={{ color: muted }}>Empty question</em>
                </Typography>
              )}
              {q.image && (
                <Box
                  component="img"
                  src={q.image}
                  alt=""
                  sx={{ display: 'block', maxWidth: '100%', maxHeight: 280, borderRadius: 1.5, border: `1px solid ${border}`, mb: 1.25 }}
                />
              )}
              {q.type === 'mcq' ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                  {(q.options || []).map((opt, i) => {
                    const isCorrect = i === Number(q.correctOptionIndex);
                    const optionImage = q.optionImages?.[i];
                    return (
                      <Box
                        key={i}
                        sx={{
                          px: 1.25,
                          py: 0.6,
                          borderRadius: 1.25,
                          fontSize: '0.85rem',
                          border: `1px solid ${isCorrect ? alpha('#1a7f37', 0.5) : border}`,
                          bgcolor: isCorrect ? alpha('#1a7f37', isDark ? 0.18 : 0.08) : 'transparent',
                          fontWeight: isCorrect ? 700 : 500,
                        }}
                      >
                        {optionImage && (
                          <Box component="img" src={optionImage} alt="" sx={{ display: 'block', maxWidth: '100%', maxHeight: 140, borderRadius: 1, mb: opt ? 0.5 : 0 }} />
                        )}
                        {opt ? (
                          <Box component="span" dangerouslySetInnerHTML={{ __html: formatScientificText(opt) }} />
                        ) : !optionImage ? (
                          <em style={{ color: muted }}>Empty option</em>
                        ) : null}
                        {isCorrect ? '  ✓ Correct answer' : ''}
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Typography variant="body2" sx={{ color: '#1a7f37' }}>
                  <b>Accepted:</b>{' '}
                  {(q.correctAnswersText || '').split(',').map((s) => s.trim()).filter(Boolean).length ? (
                    <Box
                      component="span"
                      dangerouslySetInnerHTML={{
                        __html: formatScientificText(
                          (q.correctAnswersText || '').split(',').map((s) => s.trim()).filter(Boolean).join(', '),
                        ),
                      }}
                    />
                  ) : (
                    <em style={{ color: muted }}>none set</em>
                  )}
                  {q.caseSensitive ? ' (case sensitive)' : ''}
                </Typography>
              )}
              {q.explanation && (
                <Typography
                  variant="body2"
                  sx={{ mt: 1.1, color: muted, fontStyle: 'italic' }}
                  dangerouslySetInnerHTML={{ __html: formatScientificText(q.explanation) }}
                />
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const NeetTestFormPage = () => {
  const { border, muted, isDark } = useAdminPageTheme();
  const navigate = useNavigate();
  const { id: testId } = useParams();
  const isEdit = Boolean(testId);

  const [loading, setLoading] = useState(isEdit);
  const [mode, setMode] = useState(isEdit ? 'form' : 'paste');
  const [jsonText, setJsonText] = useState(isEdit ? '' : SAMPLE_JSON);
  const [payload, setPayload] = useState(() => normalizePayload(null));
  const [fileName, setFileName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [serverErrors, setServerErrors] = useState([]);

  const fetchExisting = useCallback(async () => {
    setLoading(true);
    try {
      // Scoped timeout (not a global axios default — code execution calls
      // elsewhere legitimately take longer) so a hung request surfaces an
      // error instead of leaving this page spinning forever.
      const { data } = await proxy.get(`/neet-tests/admin/${testId}`, { timeout: 15000 });
      const normalized = normalizePayload(data);
      setPayload(normalized);
      setJsonText(JSON.stringify(denormalizePayload(normalized), null, 2));
      setIsActive(Boolean(data.isActive));
    } catch (error) {
      toast.error(
        error.code === 'ECONNABORTED'
          ? 'Timed out loading this test — check your connection and try again.'
          : 'Failed to load test',
      );
      navigate('/admin/manage-neet-tests');
    } finally {
      setLoading(false);
    }
  }, [testId, navigate]);

  useEffect(() => {
    if (isEdit) fetchExisting();
  }, [isEdit, fetchExisting]);

  const pasteJsonPreview = useMemo(() => (mode === 'paste' ? parseForPreview(jsonText) : null), [mode, jsonText]);

  const schemaPayload = useMemo(() => denormalizePayload(payload), [payload]);
  const structuredValidationErrors = useMemo(() => validateNeetTestPayload(schemaPayload), [schemaPayload]);

  const activeValidation = useMemo(() => {
    if (mode === 'paste') {
      if (pasteJsonPreview === null) return { syntaxError: null, errors: [] };
      if (pasteJsonPreview.error) return { syntaxError: pasteJsonPreview.error, errors: [] };
      return { syntaxError: null, errors: pasteJsonPreview.validationErrors || [] };
    }
    return { syntaxError: null, errors: structuredValidationErrors };
  }, [mode, pasteJsonPreview, structuredValidationErrors]);

  const handleModeChange = (_, newMode) => {
    if (mode === 'paste' && newMode !== 'paste') {
      try {
        const parsed = JSON.parse(jsonText);
        setPayload(normalizePayload(parsed));
      } catch (error) {
        toast.warn("Couldn't sync invalid JSON — showing the last valid version. Fix the JSON to update it.");
      }
    }
    if (newMode === 'paste') {
      setJsonText(JSON.stringify(denormalizePayload(payload), null, 2));
    }
    setMode(newMode);
  };

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(String(reader.result || ''));
        const normalized = normalizePayload(parsed);
        setPayload(normalized);
        setJsonText(JSON.stringify(denormalizePayload(normalized), null, 2));
        setFileName(selected.name);
        toast.success(`Loaded ${normalized.questions.length} question${normalized.questions.length === 1 ? '' : 's'} from ${selected.name}`);
      } catch (error) {
        toast.error('Invalid JSON file: ' + error.message);
      }
    };
    reader.readAsText(selected);
  };

  const updateMeta = (field, value) => setPayload((prev) => ({ ...prev, [field]: value }));

  const updateQuestion = (key, patch) =>
    setPayload((prev) => ({
      ...prev,
      questions: prev.questions.map((q) => (q._key === key ? { ...q, ...patch } : q)),
    }));

  const addQuestion = (type) =>
    setPayload((prev) => ({ ...prev, questions: [...prev.questions, createBlankQuestion(type)] }));

  const removeQuestion = (key) =>
    setPayload((prev) => ({ ...prev, questions: prev.questions.filter((q) => q._key !== key) }));

  const moveQuestion = (key, direction) =>
    setPayload((prev) => {
      const index = prev.questions.findIndex((q) => q._key === key);
      const swapWith = index + direction;
      if (index === -1 || swapWith < 0 || swapWith >= prev.questions.length) return prev;
      const questions = [...prev.questions];
      [questions[index], questions[swapWith]] = [questions[swapWith], questions[index]];
      return { ...prev, questions };
    });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerErrors([]);
    setSubmitting(true);
    try {
      let finalPayload;
      if (mode === 'paste') {
        finalPayload = JSON.parse(jsonText);
      } else {
        finalPayload = denormalizePayload(payload);
      }
      finalPayload = { ...finalPayload, isActive };

      if (isEdit) {
        await proxy.put(`/neet-tests/admin/${testId}`, finalPayload);
      } else {
        await proxy.post('/neet-tests', finalPayload);
      }
      toast.success(isEdit ? 'Test updated' : 'Test created');
      navigate('/admin/manage-neet-tests');
    } catch (error) {
      if (error instanceof SyntaxError) {
        toast.error('Invalid JSON: ' + error.message);
      } else {
        toast.error(error.response?.data?.message || 'Failed to save test');
        setServerErrors(error.response?.data?.errors || []);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <AdminPage>
        <AdminLoading />
      </AdminPage>
    );
  }

  return (
    <AdminPage maxWidth={980}>
      <Helmet>
        <title>{isEdit ? 'Edit' : 'Create'} NEET Test | Admin | CrafterHack</title>
      </Helmet>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <IconButton onClick={() => navigate('/admin/manage-neet-tests')} sx={{ border: `1px solid ${border}` }}>
          <ArrowBackRoundedIcon />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 900, fontSize: '1.3rem', letterSpacing: '-0.03em' }}>
            {isEdit ? 'Edit NEET test' : 'Create NEET test'}
          </Typography>
          <Typography variant="body2" sx={{ color: muted }}>
            Edit questions in the Form Editor, or paste/upload JSON — switch to Preview any time to see the paper as it'll appear.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={() => navigate('/admin/manage-neet-tests')} sx={{ textTransform: 'none', fontWeight: 700, color: muted }}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="neet-test-form"
            variant="contained"
            disabled={submitting || Boolean(activeValidation.syntaxError) || activeValidation.errors.length > 0}
            sx={{ textTransform: 'none', fontWeight: 800, minWidth: 140, bgcolor: ACCENT, boxShadow: 'none' }}
          >
            {submitting ? 'Saving…' : isEdit ? 'Save changes' : 'Create test'}
          </Button>
        </Box>
      </Box>

      <Box id="neet-test-form" component="form" onSubmit={handleSubmit}>
        <AdminFormSection
          title="Visibility"
          description="Inactive tests are hidden from users but keep their attempt history."
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} color="primary" />
            <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
              {isActive ? 'Active — visible to users with NEET access' : 'Inactive — hidden from users'}
            </Typography>
          </Box>
        </AdminFormSection>

        <AdminFormSection title="Question bank">
          <Tabs
            value={mode}
            onChange={handleModeChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ mb: 1.5, minHeight: 36, '& .MuiTab-root': { minHeight: 36, textTransform: 'none', fontWeight: 700 } }}
          >
            <Tab value="form" label="Form Editor" icon={<EditNoteOutlinedIcon fontSize="small" />} iconPosition="start" />
            <Tab value="paste" label="Paste JSON" icon={<CodeOutlinedIcon fontSize="small" />} iconPosition="start" />
            <Tab value="upload" label="Upload file" icon={<UploadFileOutlinedIcon fontSize="small" />} iconPosition="start" />
            <Tab value="preview" label="Preview" icon={<VisibilityOutlinedIcon fontSize="small" />} iconPosition="start" />
          </Tabs>

          {mode === 'form' && (
            <Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.25, mb: 1.25 }}>
                <TextField size="small" label="Title" value={payload.title} onChange={(e) => updateMeta('title', e.target.value)} sx={adminFieldSx} />
                <TextField size="small" label="Description" value={payload.description} onChange={(e) => updateMeta('description', e.target.value)} sx={adminFieldSx} />
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.25, mb: 1.25 }}>
                <TextField
                  size="small"
                  label="Opens at"
                  type="datetime-local"
                  value={payload.startTime}
                  onChange={(e) => updateMeta('startTime', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={adminFieldSx}
                />
                <TextField
                  size="small"
                  label="Closes at"
                  type="datetime-local"
                  value={payload.endTime}
                  onChange={(e) => updateMeta('endTime', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={adminFieldSx}
                />
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr auto' }, gap: 1.25, alignItems: 'center', mb: 2 }}>
                <TextField
                  size="small"
                  type="number"
                  label="Duration (min)"
                  value={payload.durationMinutes}
                  onChange={(e) => updateMeta('durationMinutes', e.target.value)}
                  sx={adminFieldSx}
                />
                <TextField
                  size="small"
                  type="number"
                  label="Marks correct"
                  value={payload.marksCorrect}
                  onChange={(e) => updateMeta('marksCorrect', e.target.value)}
                  sx={adminFieldSx}
                />
                <TextField
                  size="small"
                  type="number"
                  label="Marks incorrect"
                  value={payload.marksIncorrect}
                  onChange={(e) => updateMeta('marksIncorrect', e.target.value)}
                  sx={adminFieldSx}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={payload.allowNegativeMarking}
                      onChange={(e) => updateMeta('allowNegativeMarking', e.target.checked)}
                    />
                  }
                  label="Negative marking"
                  sx={{ whiteSpace: 'nowrap' }}
                />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25, flexWrap: 'wrap', gap: 1 }}>
                <Typography sx={{ fontWeight: 800, fontSize: '0.9rem' }}>
                  {payload.questions.length} question{payload.questions.length === 1 ? '' : 's'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={() => addQuestion('mcq')}
                    sx={{ textTransform: 'none', fontWeight: 700 }}
                  >
                    Add MCQ
                  </Button>
                  <Button
                    size="small"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={() => addQuestion('fill-in-blank')}
                    sx={{ textTransform: 'none', fontWeight: 700 }}
                  >
                    Add fill-in-blank
                  </Button>
                </Box>
              </Box>

              {payload.questions.length === 0 ? (
                <Typography variant="body2" sx={{ color: muted }}>
                  No questions yet — add one above, or switch to Paste JSON / Upload file to import a question bank.
                </Typography>
              ) : (
                payload.questions.map((q, index) => (
                  <QuestionEditorCard
                    key={q._key}
                    question={q}
                    index={index}
                    total={payload.questions.length}
                    onChange={(patch) => updateQuestion(q._key, patch)}
                    onRemove={() => removeQuestion(q._key)}
                    onMoveUp={() => moveQuestion(q._key, -1)}
                    onMoveDown={() => moveQuestion(q._key, 1)}
                  />
                ))
              )}
            </Box>
          )}

          {mode === 'paste' && (
            <>
              <TextField
                multiline
                minRows={16}
                maxRows={30}
                fullWidth
                value={jsonText}
                onChange={(e) => setJsonText(e.target.value)}
                placeholder={SAMPLE_JSON}
                sx={{
                  ...adminFieldSx,
                  '& .MuiOutlinedInput-root': { ...adminFieldSx['& .MuiOutlinedInput-root'], fontFamily: 'monospace', fontSize: '0.82rem' },
                }}
              />
              <Box sx={{ mt: 2 }}>
                {pasteJsonPreview === null ? (
                  <Typography variant="caption" sx={{ color: muted }}>
                    Start typing or paste JSON — switch to Form Editor or Preview any time to see it rendered.
                  </Typography>
                ) : pasteJsonPreview.error ? (
                  <Alert severity="error" sx={{ borderRadius: 1.5 }}>
                    Invalid JSON: {pasteJsonPreview.error}
                  </Alert>
                ) : (
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1.5,
                      border: `1px solid ${pasteJsonPreview.ok ? border : '#cf222e'}`,
                      bgcolor: isDark ? '#161b22' : '#fff',
                    }}
                  >
                    <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', mb: 0.75 }}>
                      {pasteJsonPreview.obj.title || 'Untitled test'} · {pasteJsonPreview.questionCount} question
                      {pasteJsonPreview.questionCount === 1 ? '' : 's'}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, mb: 0.6 }}>
                      {Object.entries(pasteJsonPreview.byType).map(([type, count]) => (
                        <Chip key={type} size="small" label={`${type}: ${count}`} sx={{ fontWeight: 700, fontSize: '0.68rem' }} />
                      ))}
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                      {Object.entries(pasteJsonPreview.bySubject).map(([subject, count]) => (
                        <Chip key={subject} size="small" variant="outlined" label={`${subject}: ${count}`} sx={{ fontWeight: 700, fontSize: '0.68rem' }} />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            </>
          )}

          {mode === 'upload' && (
            <Box>
              <Button
                component="label"
                variant="outlined"
                startIcon={<UploadFileOutlinedIcon />}
                sx={{ textTransform: 'none', fontWeight: 700, borderColor: border }}
              >
                {fileName || 'Choose .json file'}
                <input type="file" accept="application/json,.json" hidden onChange={handleFileChange} />
              </Button>
              {fileName && (
                <Typography variant="body2" sx={{ color: muted, mt: 1.5 }}>
                  Loaded {payload.questions.length} question{payload.questions.length === 1 ? '' : 's'} from {fileName} — switch to
                  Form Editor or Preview to review before saving.
                </Typography>
              )}
            </Box>
          )}

          {mode === 'preview' && <TestPreview payload={payload} />}

          {activeValidation.syntaxError ? null : activeValidation.errors.length > 0 && (
            <Alert severity="error" sx={{ borderRadius: 1.5, mt: 2 }}>
              <Typography sx={{ fontWeight: 800, fontSize: '0.82rem', mb: 0.5 }}>
                This won't be accepted — fix before submitting:
              </Typography>
              {activeValidation.errors.map((err, i) => (
                <Typography key={i} variant="body2" sx={{ fontSize: '0.78rem' }}>
                  · {err}
                </Typography>
              ))}
            </Alert>
          )}

          {serverErrors.length > 0 && (
            <Alert severity="error" sx={{ borderRadius: 1.5, mt: 2 }}>
              <Typography sx={{ fontWeight: 800, fontSize: '0.82rem', mb: 0.5 }}>Validation failed:</Typography>
              {serverErrors.map((err, i) => (
                <Typography key={i} variant="body2" sx={{ fontSize: '0.78rem' }}>
                  · {err}
                </Typography>
              ))}
            </Alert>
          )}
        </AdminFormSection>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
          <Button onClick={() => navigate('/admin/manage-neet-tests')} sx={{ textTransform: 'none', fontWeight: 700, color: muted }}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={submitting || Boolean(activeValidation.syntaxError) || activeValidation.errors.length > 0}
            sx={{ textTransform: 'none', fontWeight: 800, minWidth: 140, bgcolor: ACCENT, boxShadow: 'none' }}
          >
            {submitting ? 'Saving…' : isEdit ? 'Save changes' : 'Create test'}
          </Button>
        </Box>
      </Box>
    </AdminPage>
  );
};

export default NeetTestFormPage;
